<template>
    <div class="article-blog-page" @scroll="scrolling()">
        <breadcrumbs :breadcrumbs="breadcrumbs" :scroll="scroll" @goBack="back" />
        <section class="article-blog__banner article-blog__banner--eclipse">
            <img class="article-blog__banner-img" src="@/assets/blog/chat-gpt-integration/chat-gpt-integration-cover.jpg" alt="" loading="lazy" modernize='sizes=["768w", "1200w", "1536w", "1920w", "2400w", "3840w", "original"] imageFormats=["webp", "original"]' />
            <h1 class="article-blog__banner-title">
                ENHANCING E-COMMERCE WITH CHATGPT: UNWRAPPING THE BENEFITS FOR MERCHANTS
            </h1>
        </section>
        <div class="container article-blog section_holder">
            <div class="section_title">
                <SidebarBlog :tagFromPost="tags" @searchByTag="goToPath" />
            </div>
            <div class="section_content">
                <div class="section_content__item">
                    <p>
                        Since ChatGPT has been released to the general public, everyone is talking about its real value both for everyday workload and strategic management. There are vocal arguments, both positive and negative, in the debate – still, it is not yet clear where AI-driven technologies lead us to.
                    </p>
                    <p>
                        One thing, though, seems to have proven itself - AI is already having a noticeable impact on the e-commerce industry. ChatGPT is an easy-to-use assistant that can help your e-commerce business become more efficient by improving your customer experience, enhancing overall scalability, and extracting data-driven insights that inform decision-making.
                    </p>
                    <h2 class="section_content__item-title">So, What Actually Can ChatGPT Do For E-Commerce?</h2>
                    <p>
                        <i>Automate Tasks</i><br>
                        First up, ChatGPT in e-commerce can automate time-consuming tasks. It's like having a trustable robot handling customer support, inventory management, and order tracking, freeing up your time and resources to focus on bigger and better things.
                    </p>
                    <p>
                        <i>Product Recommendations</i><br>
                        OpenAI's GPT models can understand customer preferences and shopping habits, enabling you to create highly personalized product recommendations. By analyzing user behavior and interactions, these models can suggest items relevant to individual visitors, ultimately increasing conversion rates and customer satisfaction.
                    </p>
                    <p>
                        <i>Content Generation</i><br>
                        AI-driven content generation is another powerful tool. OpenAI's models can generate product descriptions, blog posts, and marketing content with human-like quality, saving time and resources for content creation. You can ensure a consistent tone and style across your website and marketing materials.
                    </p>
                    <p>
                        <i>Inventory Management</i><br>
                        AI can predict demand patterns and optimize inventory management. By analyzing historical data, GPT models can provide insights into stock levels, preventing overstocking or understocking issues. This ensures that you always have the right products in stock, reducing costs and improving customer experience.
                    </p>
                    <p>
                        <i>Analytics & Insights</i><br>
                        GPT can help e-commerce businesses by analyzing customer data. It learns to spot patterns and trends in how customers behave and buy things, giving business owners useful information. This information can be really important for making marketing better, choosing the right products to sell, and making smarter business decisions. Plus, these data-driven insights can also be used to check for risks, spot fraud, and make the e-commerce platform safer.
                    </p>
                    <div class="section_content__img">
                        <img src="@/assets/blog/chat-gpt-integration/image-01.jpeg" alt="Article image 1" title="Article image 1" loading="lazy" modernize='sizes=["480w", "768w", "1200w", "1536w", "1920w", "2400w", "3840w", "original"] imageFormats=["webp", "original"]' />
                    </div>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">Our Case: A Complex Integration with ChatGPT</h2>
                    <p>
                        One of our clients, a major US-based online retailer, reached <b>WDEVS</b> while facing a tangible problem. With a huge amount of products and user data, the server was overloaded, and the customers experienced long page-loading times. Repetitive inquiries were also leading to customer frustration and quick exit without any interest in the brand. That’s why our client needed a solution that could scale to meet their support demands while maintaining a high standard of service. We developed and integrated a custom AI-driven tool capable of handling customer inquiries, order tracking, and product recommendations.
                    </p>
                    <p>
                        Our work included several steps:
                    </p>
                    <p>
                        <strong>Data Collection and Training.</strong> We started by gathering historical chat logs and customer interactions. This data was used to train ChatGPT on the specific language and context used by the customers.
                    </p>
                    <p>
                        <strong>Integration with CRM.</strong> ChatGPT was seamlessly integrated with the client’s Customer Relationship Management (CRM) system. This enabled our AI-driven tool to access customer profiles, order history, and real-time inventory data.
                    </p>
                    <p>
                        <strong>Implementing the NLP Engine.</strong> A natural language processing (NLP) engine was implemented to enhance the chatbot's ability to understand and generate human-like responses.
                    </p>
                    <p>
                        <strong>User Profiling.</strong> We created user profiles to track customer preferences and past interactions. This allowed the ChatGPT-based tool to offer highly personalized product recommendations based on individual preferences.
                    </p>
                    <p>
                        By integrating OpenAI's ChatGPT, our client not only improved their customer support but also optimized their e-commerce operations, by leveraging some crucial benefits, such as:
                    </p>
                    <ul>
                        <li>Better Scalability</li>
                        <li>Comprehensive Personalization</li>
                        <li>Reduced Costs</li>
                        <li>24/7 Customer Service Availability</li>
                        <li>Extensive Data Insights</li>
                    </ul>
                    <p>
                        The result was a seamless and efficient shopping experience for the customers, ultimately boosting sales and customer loyalty rates.
                    </p>
                </div>
                <div class="section_content__item">
                    <h2 class="section_content__item-title">I Don’t Think I Need Something So Complicated For My Business…</h2>
                    <p>
                        Small startups and e-commerce business owners often hesitate to embrace AI, fearing it's too complex or costly. While we understand these concerns, there are easy & adaptable ways of streamlining your working processes and saving time & money.
                    </p>
                    <p>
                        One of the most useful features that ChatGPT can provide is to create a unique description for the product based on the details the product has. Such little things play a crucial role in the business development, because when sites do not provide good product descriptions, it leads to abandoned carts, unnecessary returns and lost interest in your offers. Moreover, it can damage your brand’s reputation in the long run.
                    </p>
                    <p>
                        Creating unique and compelling product descriptions can be challenging, but that was our case with a growing real estate platform, whose owner requested our team to build an effective integration with OpenAI API to generate exclusive, detailed and SEO-optimized property descriptions and attract new site visitors.
                    </p>
                    <agile
                        ref="carousel"
                        class="section_content__carousel"
                        :nav-buttons="true"
                        :dots="false"
                        :options="myOptions"
                    >
                        <div>
                            <img src="../../assets/blog/chat-gpt-integration/image-slider-01.png" alt="" />
                        </div>
                        <div>
                            <img src="../../assets/blog/chat-gpt-integration/image-slider-02.png" alt="" />
                        </div>
                    </agile>
                    <p>
                        This recent project serves as a perfect example of how ChatGPT integration can be a quick & simple solution for your business.
                    </p>
                    <p>
                        Our integration includes all the essential features needed for our user-friendly WYSIWYG (What You See Is What You Get) interface. This custom feature helps admin users to quickly create property descriptions that are both tailored to their chosen criteria and optimized for search engines.
                    </p>
                    <p>
                        Using this feature, the client now can showcase his properties online, assured that every description is tailored for maximum impact. No more hours spent crafting property descriptions - our integration not only saves time but also reduces expenses on SEO services. And the idea is designed with simplicity in mind, making it easy for the client’s management team to use the tool without any technical expertise.
                    </p>
                    <div class="section_content__img">
                        <img src="@/assets/blog/chat-gpt-integration/image-02.jpg" alt="Article image 1" title="Article image 1" loading="lazy" modernize='sizes=["480w", "768w", "1200w", "1536w", "1920w", "2400w", "3840w", "original"] imageFormats=["webp", "original"]' />
                    </div>
                    <p>
                        As you can see, ChatGPT technical capabilities in e-commerce are numerous and impressive. It understands natural language, interacts with customers, provides recommendations, and assists with a wide range of specific tasks. When used correctly, AI-driven tools offer the opportunity for e-commerce merchants to enhance their operations, which results in greater efficiency for your business strategy.
                    </p>
                    <p>
                        At <b>WDEVS</b>, we have a team of experienced developers who can help you to integrate GPT tools into your e-commerce platform. We have over 15 years of experience in e-commerce web development and have worked with companies that lead the market today. <strong><a href="/contacts">Contact us</a> if you want to know more how integrating AI-powered technologies into your business can help your brand stay ahead of the competition</strong>.
                    </p>
                </div>
                <div>
                    <div class="footer-blog-item">
                        <div class="btn-read-more primary contacts-button small" id="show-modal" @click="this.$router.push('/blog')">
                            <span class="button-content">
                                <span class="icon">
                                    <img v-svg-inline :data-src="require('@/assets/icons/rarr-icon.svg')" alt="" />
                                </span>
                                Read more our stories
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SidebarBlog from "./Sidebar.vue";
import Breadcrumbs from "/src/components/Breadcrumbs";
import { VueAgile } from "vue-agile";
import { mapState } from "vuex";

export default {
    name: "BlogChatGptIntegration",
    agile: VueAgile,
    data() {
        return {
            backHistory: history.state.back,
            tags: ['ChatGPT Integration', 'eCommerce', 'AI-driven solutions', 'Custom Web Development', 'OpenAI', 'Business Automation', 'Natural Language Processing'],
            scroll: false,
            position: null,
            breadcrumbs: [
                {
                    link: '/',
                    name: 'Home',
                },
                {
                    name: 'Blog',
                    link: '/blog',
                },
                {
                    name: 'Enhancing E-Commerce With ChatGPT',
                },
            ],
            myOptions: {
                responsive: [
                    {
                        breakpoint: 300,
                        settings: {
                            slidesToShow: 1,
                        },
                    },
                ],
            },
        };
    },
    components: {
        SidebarBlog,
        Breadcrumbs,
    },
    computed: {
        ...mapState(['blog'])
    },
    methods: {
        goToPath(tag) {
            let post = this.blog.find(item => item.tags.includes(tag))
            this.$router.push(post.img_src)
        },
        back() {
            if (!document.referrer || this.backHistory !== '/blog') {
                Object.defineProperty(document, "referrer", {
                    get: function () {
                        return `${window.location.origin}/blog`;
                    },
                });
                this.$router.push('/blog')
                return
            } else {
                this.$router.go(-1)
            }
        },
        scrolling() {
            const scroll = document.documentElement.scrollTop;
            if (scroll > 50) {
                this.scroll = true;
            } else {
                this.scroll = false;
            }
        },
    },
    created() {
        console.log(history.state.back)
        window.history.scrollRestoration = "auto";
        window.addEventListener("scroll", this.scrolling);
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.scrolling);
    },
}
</script>

<style lang="scss">
@import "../../scss/_variables.scss";
@import "include-media/dist/_include-media.scss";

.article-blog-header {
    .article-blog-review__title {
        padding: 120px 0;
        text-align: end;
        text-transform: uppercase;
        color: $black;
        font-size: 32px;
        line-height: 111%;
        margin: 0;

        @include media(">=tablet") {
            font-size: 40px;
            max-width: 100%;
        }

        @include media(">=tablet-xl") {
            font-size: 56px;
        }

        @include media(">=widescreen") {
            font-size: 64px;
            max-width: 100%;
        }
    }
}

.footer-blog-item {
    display: block;
    padding-top: 20px;

    @include media(">=tablet") {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .article-blog-tags {
        margin-top: 40px;
        color: $red;
        font-weight: 700;
        font-size: 16px;
        line-height: 28px;

        @include media(">=tablet") {
            margin-top: 0;
            text-align: center;
        }

        span {
            margin-right: 20px;

            @include media(">=widescreen") {
                margin-right: 40px;
            }
        }
    }
}

.article-blog-page {
    a {
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    .link {
        color: $red;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .btn-read-more {
        .button-content {
            border-radius: 26px;
            padding: 2px 12px 3px;
            background-color: $red;
            color: $white;
            font-weight: 700;
            font-size: 16px;
            line-height: 28px;
            cursor: pointer;
            width: max-content;
            background-image: linear-gradient($darkRed, $darkRed);
            background-repeat: no-repeat;
            transition: background-size 0s, color 0s;
            background-size: 0 100%;

            @include media(">=tablet-xl") {
                padding: 6px 24px 8px;
            }

            &:hover {
                background-size: 100% 100%;
                color: $white;
            }
        }

        .icon {
            padding-top: 6px;
            margin-right: 15px;
            transform: rotateY(180deg);
        }

        a {
            color: $white;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .go-back-to-read {
        padding: 16px 0;
        border-bottom-left-radius: 26px 26px;
        border-bottom-right-radius: 26px 26px;
        position: fixed;
        background: $white;
        z-index: 50;
        width: 100vw;
        transition: all 0.3s;

        @include media(">=tablet-xl") {
            padding: 16px 0;
        }

        &.scrolling {
            padding: 5px 0;
            border-bottom: 1px solid $red;

            @include media(">=tablet-xl") {
                padding: 5px 0;
            }
        }
    }
}

.article-blog {
    &.section_holder {
        display: flex;
        flex-direction: column-reverse;

        @include media(">=tablet-xl") {
            flex-direction: row;
        }
    }

    .section_title {
        margin-right: 0;
        width: 100%;

        @include media(">=tablet-xl") {
            margin-right: 43px;
            width: 35%;
        }
    }

    .section_content {
        margin-bottom: 60px;
        display: block;
        margin-left: 0;
        width: 100%;

        a {
            color: $red;
        }

        &__item {
            margin-bottom: 40px;

            @include media(">=tablet") {
                margin-bottom: 60px;
            }

            @include media(">=desktop") {
                margin-bottom: 120px;
            }

            &-title {
                &-icon {
                    flex-shrink: 0;
                    width: 400px;
                    height: auto;
                    margin-right: 15px;

                    @media all and (max-width: 480px) {
                        width: 100%;
                        margin-right: 0;
                    }

                    svg {
                        max-width: 100%;
                        width: 100%;
                        max-height: 100%;
                        height: 100%;
                    }
                }

                &--icon {
                    display: flex;
                    align-items: flex-end;

                    @media all and (max-width: 767px) {
                        flex-wrap: wrap;
                    }

                    @media (min-width: 992px) and (max-width: 1199px) {
                        flex-wrap: wrap;
                    }
                }
            }

            &-slider {
                position: relative;
            }

            &-link {
                display: inline-block;

                &:hover {
                    text-decoration: none;
                }
            }

            blockquote {
                margin: 25px 0;
                border-left: 3px solid lighten($base-text-color, 25%);
                padding: 10px 25px;
                color: lighten($base-text-color, 25%);
            }
        }

        @include media(">=tablet-xl") {
            margin-left: 37px;
            width: 65%;
            margin-bottom: 0;
        }

        &__paragraph {
            display: flex;
            margin-bottom: 45px;

            &-icon {
                flex-shrink: 0;
                display: inline-block;
                width: 65px;
                height: 65px;
                margin-right: 10px;
                vertical-align: middle;

                @media all and (min-width: 768px) {
                    width: 100px;
                    height: 100px;
                    margin-right: 15px;
                }

                svg {
                    max-width: 100%;
                    width: 100%;
                    max-height: 100%;
                    height: 100%;
                }
            }
        }
    }
}

.article-blog__banner {
    position: relative;
    height: 300px;
    margin-bottom: 60px;

    @include media(">=tablet") {
        height: 500px;
    }

    @include media(">=desktop") {
        height: 600px;
        margin-bottom: 120px;
    }

    &--eclipse {
        z-index: 1;

        &:before {
            content: "";
            position: absolute;
            z-index: 2;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background-color: rgba(0, 0, 0, 0.3);
        }

        .article-blog__banner-title {
            z-index: 3;
        }
    }

    &-img,
    picture {
        display: block;
        height: 100%;

        img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
}

.article-blog__banner-title {
    text-transform: uppercase;
    color: $white;
    font-size: 32px;
    line-height: 111%;
    position: absolute;
    bottom: 20px;
    left: 16px;
    max-width: calc(100% - 32px);
    margin: 0;

    @include media(">=tablet") {
        bottom: 40px;
        font-size: 40px;
        left: 40px;
        max-width: calc(100% - 80px);
    }

    @include media(">=tablet-xl") {
        bottom: 80px;
        left: 40px;
        font-size: 56px;
        max-width: calc(100% - 80px);
    }

    @include media(">=widescreen") {
        bottom: 80px;
        left: 80px;
        font-size: 64px;
        max-width: 1150px;
    }
}

.article-blog__img,
picture {
    margin: 0 0 40px;
    display: block;
}

.article-blog__customer {
    em {
        display: block;
        margin-top: 32px;
    }

    .button {
        margin-top: 40px;
    }
}

.section_content__carousel {
    margin-bottom: 40px;
    .agile__slide {
        img {
            width: 100%;
        }
    }
    .agile__nav-button {
        border-radius: 50%;
        background-color: $red;
        color: transparent;
        transition: background-color .3s;
        &:after {
            content: url("data:image/svg+xml,%3Csvg width='20' height='32' viewBox='0 0 20 32' fill='none' xmlns='https://www.w3.org/2000/svg'%3E%3Cpath d='M18.2622 18.2616L5.46234 31.0614C4.83728 31.6865 4.0188 31.999 3.19996 31.999C2.38113 31.999 1.56265 31.6865 0.93759 31.0614C-0.31253 29.8124 -0.31253 27.7854 0.93759 26.5363L11.4747 15.9992L0.93759 5.46172C-0.31253 4.21267 -0.31253 2.18567 0.93759 0.936613C2.18771 -0.313506 4.21258 -0.313506 5.46269 0.936613L18.2626 13.7365C19.5123 14.9855 19.5123 17.0129 18.2622 18.2616Z' fill='white'/%3E%3C/svg%3E%0A");
            position: absolute;
            top: calc(50% - 16px);
            width: 20px;
            height: 32px;
        }
        &:hover {
            background-color: $darkenRed;
        }
        &--prev {
            left: 10px;
            &:after {
                left: calc(50% - 12px);
                transform: rotate(180deg);
            }
        }
        &--next {
            right: 10px;
            &:after {
                left: calc(50% - 6px);
            }
        }
    }
}
</style>
